import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import { Chip } from "@mui/material";

import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";

import styles from "./RobotoLLMContextWidget.module.css";

export type ContextType = "dataset" | "file";

interface RobotoLLMContextWidgetProps {
  context: RobotoLLMContext;
  onDelete: (contextType: ContextType, contextContent: string) => void;
}

interface RobotoLLMContextChipProps {
  contextType: ContextType;
  contextContent: string;
  onDelete: () => void;
}

export const RobotoLLMContextChip = ({
  contextType,
  contextContent,
  onDelete,
}: RobotoLLMContextChipProps) => {
  const icon =
    contextType === "dataset" ? (
      <FolderIcon className={styles.chipIconDataset} />
    ) : (
      <DescriptionIcon className={styles.chipIconFile} />
    );

  return (
    <Chip
      icon={icon}
      label={contextContent}
      size="small"
      onDelete={onDelete}
      className={styles.chip}
      classes={{
        label: styles.chipLabel,
      }}
      sx={{
        "& .MuiChip-icon": {
          marginRight: "1px",
        },
      }}
    />
  );
};

/**
 * Widget that displays the current RobotoLLMContext with dataset and file IDs.
 * Inspired by AugmentCode's PyCharm plugin context bar.
 */
export const RobotoLLMContextWidget = ({
  context,
  onDelete,
}: RobotoLLMContextWidgetProps) => {
  // Don't render if there's no context
  const hasContext =
    context.dataset_ids.length > 0 || context.file_ids.length > 0;

  if (!hasContext) {
    return null;
  }

  return (
    <div className={styles.contextContainer}>
      {context.dataset_ids.map((datasetId) => (
        <RobotoLLMContextChip
          key={datasetId}
          contextType="dataset"
          contextContent={datasetId}
          onDelete={() => onDelete("dataset", datasetId)}
        />
      ))}

      {/* File IDs */}
      {context.file_ids.map((fileId) => (
        <RobotoLLMContextChip
          key={fileId}
          contextType="file"
          contextContent={fileId}
          onDelete={() => onDelete("file", fileId)}
        />
      ))}
    </div>
  );
};
